import axios from "../../../utils/axios";
import {
  ApiRateComparisonItem,
  ApiRateQuote,
  CurrencyPairOption,
  CurrencyPairRate,
  GetRateComparisonParams,
  GetRateHistoryParams,
  RateProvider,
  RateProviderQuote,
  RatePaginationMeta,
} from "./types";

// Live endpoint backing both the Comparison and History tabs — confirmed
// supported query params (per the live API docs):
//   page, per_page (1-50), period (latest|24h|7d|custom) + custom_date,
//   filter[currency_pair], filter[position] (leading|behind), export
// Filtering/pagination/period are all handled server-side.
const COMPARISON_ENDPOINT = "/admin/exchange-rates/comparison";

function toProviderQuote(raw: ApiRateQuote | null): RateProviderQuote {
  if (!raw) {
    return { rate: null, updated_minutes_ago: null, is_stale: false, is_best: false };
  }

  const fetchedAt = new Date(raw.fetched_at).getTime();
  const updated_minutes_ago = Number.isNaN(fetchedAt)
    ? null
    : Math.max(0, Math.round((Date.now() - fetchedAt) / 60000));

  return {
    rate: raw.value,
    updated_minutes_ago,
    is_stale: raw.is_stale,
    // Computed server-side — trusted directly rather than recomputed here.
    is_best: raw.is_best_fresh,
  };
}

export function mapApiRateComparisonItem(item: ApiRateComparisonItem): CurrencyPairRate {
  const quotes: Record<RateProvider, RateProviderQuote> = {
    smartsend: toProviderQuote(item.smart_send),
    wise: toProviderQuote(item.competitors?.wise),
    remitly: toProviderQuote(item.competitors?.remitly),
    lemfi: toProviderQuote(item.competitors?.lemfi),
    taptap_send: toProviderQuote(item.competitors?.taptapsend),
    others: toProviderQuote(item.competitors?.others),
  };

  return {
    id: `${item.source_currency}-${item.destination_currency}`.toLowerCase(),
    pair_label: `${item.source_currency} → ${item.destination_currency}`,
    base_currency: item.source_currency,
    quote_currency: item.destination_currency,
    quotes,
    position: item.position?.status ?? null,
    position_percent: item.position?.behind_percent ?? null,
  };
}

interface ComparisonPageResult {
  status: boolean;
  message: string;
  pairs: CurrencyPairRate[];
  meta?: RatePaginationMeta;
}

export interface ComparisonQuery {
  page?: number;
  per_page?: number;
  period?: string;
  custom_date?: string;
  currency_pair?: string;
  position?: string;
}

// Exported so the CSV export thunk (redux/rateIntelligence.ts) builds its
// query params the exact same way as the table's own data fetch — the
// export should always reflect whatever filters are currently applied.
export function buildComparisonParams(query: ComparisonQuery): Record<string, string | number> {
  const params: Record<string, string | number> = {};
  if (query.page) params.page = query.page;
  if (query.per_page) params.per_page = query.per_page;
  if (query.period) params.period = query.period;
  if (query.period === "custom" && query.custom_date) params.custom_date = query.custom_date;
  // Sent as pre-bracketed keys rather than a nested `filter` object so the
  // exact `filter[currency_pair]`/`filter[position]` shape the API expects
  // doesn't depend on axios's own object-serialization behavior.
  if (query.currency_pair) params["filter[currency_pair]"] = query.currency_pair;
  if (query.position) params["filter[position]"] = query.position;
  return params;
}

async function fetchComparisonPage(query: ComparisonQuery): Promise<ComparisonPageResult> {
  const res = await axios.get(COMPARISON_ENDPOINT, { params: buildComparisonParams(query) });

  // The shared axios instance's response interceptor resolves instead of
  // rejecting on most errors, so a failed request can land here as an
  // AxiosError rather than throwing — guard against reading that as an
  // empty (rather than failed) page.
  if ((res as any)?.isAxiosError) throw res;

  const rawItems: ApiRateComparisonItem[] = res?.data?.data || [];
  const rawMeta = res?.data?.meta;

  return {
    status: res?.data?.status,
    message: res?.data?.message,
    pairs: rawItems.map(mapApiRateComparisonItem),
    meta: rawMeta
      ? {
          current_page: rawMeta.current_page,
          per_page: rawMeta.per_page,
          total: rawMeta.total,
          last_page: rawMeta.last_page,
        }
      : undefined,
  };
}

export async function fetchRateComparison(params: GetRateComparisonParams) {
  const page = await fetchComparisonPage(params);
  return {
    data: {
      status: page.status,
      message: page.message,
      data: { pairs: page.pairs, quoted_at: undefined, meta: page.meta },
    },
  };
}

export async function fetchRateHistory(params: GetRateHistoryParams) {
  return fetchRateComparison(params);
}

export async function fetchComparisonCurrencyPairOptions() {
  // Still needs every pair regardless of whatever page/filter the table is
  // on — there's no "list all pairs" endpoint, so this walks every page,
  // using the max allowed per_page (50) to keep that to as few requests as
  // possible (currently 1, given ~13 pairs total).
  const first = await fetchComparisonPage({ page: 1, per_page: 50 });
  let pairs = first.pairs;
  const lastPage = first.meta?.last_page || 1;
  for (let page = 2; page <= lastPage; page++) {
    const next = await fetchComparisonPage({ page, per_page: 50 });
    pairs = pairs.concat(next.pairs);
  }

  // Filter value must match the API's own `currency_pair` field format
  // (e.g. "CAD-NGN"), not the lowercase `id` used internally for React
  // keys — base/quote currency codes are already uppercase from the API.
  const options: CurrencyPairOption[] = pairs.map((p) => ({
    label: p.pair_label,
    value: `${p.base_currency}-${p.quote_currency}`,
  }));

  return { data: { status: true, message: "OK", data: { options } } };
}
